import { Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';
import { makeCancelable, sortByCreated } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import Note from '../vocabulary/taxonomies/Note';
import BasicLanguageField from './BasicLanguageField';
import Errored from './Errored';
import LangConfirmationField from './LangConfirmationField';
import TitledPanel from './TitledPanel';

class SkosNoteDisplay extends React.Component {

    constructor(props) {
        super(props);
        const nfr = "http://www.w3.org/2004/02/skos/core#" + props.property;
        const resources = props.data[nfr] ?
            Array.isArray(props.data[nfr]) ?
                props.data[nfr]
                :
                [props.data[nfr]]
            :
            [];
        const resourcesIRIs = resources.map(r => r.id);
        this.state = {
            resources: resourcesIRIs,
            sortedNotes: [],
            needsLoading: false,
            isErrored: undefined
        };
        this.cancellable = null;
    }

    static getDerivedStateFromProps(props, state) {
        const nfr = "http://www.w3.org/2004/02/skos/core#" + props.property;
        const resources = props.data[nfr] ?
            Array.isArray(props.data[nfr]) ?
                props.data[nfr]
                :
                [props.data[nfr]]
            :
            [];
        const resourcesIRIs = resources.map(r => r.id);
        if (resourcesIRIs.length === state.resources.length) {
            const hasChanged = resourcesIRIs.some(r => !state.resources.includes(r));
            if (!hasChanged) return null;
        }
        return {
            resources: resourcesIRIs,
            needsLoading: true
        };
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.needsLoading) this.sortNotes();
    }

    componentDidMount() {
        this.sortNotes();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    sortNotes = async () => {
        try {
            await this.loadData(this.state.resources).promise;
            const graph = this.props.graph;
            const sortedNotesByComment = [...this.state.resources].sort((a, b) => {
                const aData = GS.data(a, graph, false);
                const bData = GS.data(b, graph, false);
                const aLabel = aData && aData.comment ? aData.comment[Object.keys(aData.comment)[0]] : "";
                const bLabel = bData && bData.comment ? bData.comment[Object.keys(bData.comment)[0]] : "";
                if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
                if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
                return 0;
            });
            const sortedNotesByDate = sortByCreated(sortedNotesByComment, graph, false);
            this.setState({ needsLoading: false, sortedNotes: sortedNotesByDate });
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e, needsLoading: false });
        }
    };

    loadData(data) {
        this.cancellable = makeCancelable(GS.loadData(data, {}, this.props.graph, false));
        return this.cancellable;
    }

    render() {

        const { isReadOnly, message, title, placeholder, maxLength, existingValues, tooltipText,
            defaultLang, property, values, projectLang, onUpdate, dataTest, graph, ariaLabel, propertyTemplates,
            onDelete, onAddLanguage, multiline, name, projectIRI, componentIRI, viewLang, projectType,
            components, linkedData, associatedTaxonomyProject, ontologyProjectAssociation, requiredEntitlement } = this.props;
        const { isErrored, sortedNotes } = this.state;

        const showTemplates = true;

        if (isErrored) return <Errored dataTest="sn-error" data={isErrored} />;

        return (
            <TitledPanel
                dataTest={dataTest}
                height="full"
                title={title}
                actions=
                {!isReadOnly && onAddLanguage ?
                    <BasicLanguageField
                        dataTest={dataTest}
                        existingLangs={values ? Object.keys(values) : []}
                        multiline={multiline}
                        defaultLang={viewLang}
                        tooltipText={tooltipText}
                        langs={projectLang}
                        placeholder={placeholder}
                        existingValues={existingValues ?
                            existingValues
                            :
                            values}
                        title={message}
                        name={name}
                        onAddLanguage={onAddLanguage(property)}
                        maxLength={maxLength} />
                    :
                    null
                }>
                {values && Object.keys(values).sort().map((lang) => {
                    const langFields = Array.isArray(values[lang]) ? values[lang] : [values[lang]];
                    const existingLangFields = existingValues ?
                        Array.isArray(existingValues[lang]) ? existingValues[lang] : [existingValues[lang]]
                        :
                        Array.isArray(values[lang]) ? values[lang] : [values[lang]];
                    let positions = {};
                    langFields.forEach((text, i) => positions[text] = i);
                    return <div key={lang}>
                        {
                            langFields
                                .sort()
                                .map((text, index) =>
                                    <LangConfirmationField
                                        dataTest={dataTest + "-field-" + lang + '-' + index}
                                        key={lang + "-" + text}
                                        ariaLabel={ariaLabel}
                                        onUpdate={onUpdate(property, lang, Array.isArray(values[lang]) ? positions[text] : null)}
                                        onDelete={onDelete(property, lang)}
                                        existingValues={existingLangFields}
                                        focus={false}
                                        canBeDeleted={true}
                                        maxLength={maxLength || 500}
                                        multiline={multiline}
                                        text={text}
                                        lang={lang}
                                        isReadOnly={isReadOnly} />
                                )
                        }
                    </div>;
                }
                )}
                {sortedNotes && sortedNotes.length > 0 &&
                    <div>
                        <div>
                            <Typography data-test="notes-resources" variant="caption" style={{ color: '#01579B' }}>As Resources (Newest First)</Typography>
                        </div>
                        <div data-test="note-items">
                            {sortedNotes.map(note =>
                                <Note
                                    id={note}
                                    handleNotAvailable={true}
                                    graph={graph}
                                    linkedData={linkedData}
                                    showTemplates={showTemplates}
                                    components={components}
                                    requiredEntitlement={requiredEntitlement}
                                    ontologyProjectAssociation={ontologyProjectAssociation}
                                    componentIRI={componentIRI}
                                    property={property}
                                    projectLang={projectLang}
                                    projectType={projectType}
                                    associatedTaxonomyProject={associatedTaxonomyProject}
                                    propertyTemplates={propertyTemplates}
                                    projectIRI={projectIRI}
                                    defaultLang={defaultLang}
                                    viewLang={viewLang}
                                    maxLength={maxLength || 500}
                                    isReadOnly={isReadOnly}
                                    key={note}
                                />
                            )}
                        </div>
                    </div>
                }
            </TitledPanel>
        );
    }
}

SkosNoteDisplay.defaultProps = {
    multiline: false
};

SkosNoteDisplay.propTypes = {
    dataTest: PropTypes.string,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAddLanguage: PropTypes.func,
    multiline: PropTypes.bool,
    ariaLabel: PropTypes.string,
    data: PropTypes.object,
    values: PropTypes.object,
    propertyTemplates: PropTypes.array,
    // existingValues overrides values if supplied
    existingValues: PropTypes.object,
    name: PropTypes.string,
    isReadOnly: PropTypes.bool,
    graph: PropTypes.string,
    placeholder: PropTypes.string,
    tooltipText: PropTypes.string,
    maxLength: PropTypes.number,
    components: PropTypes.array,
    componentIRI: PropTypes.string,
    projectIRI: PropTypes.string.isRequired,
    projectType: PropTypes.string.isRequired,
    projectLang: PropTypes.array.isRequired,
    linkedData: PropTypes.array,
    message: PropTypes.string,
    requiredEntitlement: PropTypes.string.isRequired,
    ontologyProjectAssociation: PropTypes.array,
    property: PropTypes.string.isRequired,
    associatedTaxonomyProject: PropTypes.array,
    defaultLang: PropTypes.string.isRequired,
    viewLang: PropTypes.string.isRequired,
    title: PropTypes.string
};

export default SkosNoteDisplay;